import {
	Button,
	ResourceOptions,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { Download, LineChart, ListFilter, PieChart, Target, Trophy, Users } from "lucide-react";
import { useState } from "react";
import { api, type TopicRow, type VisibilityTab } from "~/core/api/index";
import { useI18n, useT } from "~/core/i18n";
import { brand } from "~/core/paths";
import { keys, read } from "~/core/query";
import { Hint, Section } from "~/core/ui/page";
import { Async } from "~/core/ui/states";
import { Delta, Donut, Matrix, metricCsv, Series, value } from "~/modules/analytics/components/chart";
import { DrillLink } from "~/modules/analytics/components/detail";
import { InsightIcon } from "~/modules/analytics/components/insight-icon";
import { type ReadingProps, StageShell } from "~/modules/analytics/components/stage";
import { download, SortHeader, useSort } from "~/modules/analytics/components/table";
import { readingPath } from "~/modules/analytics/lib/loop";

const METRICS = ["visibility", "share_of_voice", "average_position"] as const;
const METRIC_ICONS = [Target, PieChart, Trophy];
const METRIC_TONES = ["detect", "analyse", "generate"] as const;

export function InsightVisibility({ entityId, filters, setFilters }: ReadingProps) {
	const state = useQuery({
		queryKey: [
			...keys.entity(entityId),
			"insights",
			"visibility",
			{
				range: filters.range,
				engine: filters.engine,
				locale: filters.locale,
				topics: filters.topics,
				intent: filters.intent,
			},
		],
		queryFn: () => api.insights.visibility(entityId, filters),
		...read,
	});
	return (
		<StageShell
			entityId={entityId}
			reading="visibility"
			filters={filters}
			setFilters={setFilters}
			topics={state.data?.topics.map((topic) => topic.topic)}
		>
			<Async state={state}>{(data) => <VisibilityContent entityId={entityId} data={data} />}</Async>
		</StageShell>
	);
}

export function VisibilityContent({ entityId, data }: { entityId: string; data: VisibilityTab }) {
	const t = useT();
	const { locale } = useI18n();
	const [metric, setMetric] = useState<(typeof METRICS)[number]>("visibility");
	const [expanded, setExpanded] = useState(false);
	const block = data[metric];
	const brands = expanded ? data.assets : data.assets.slice(0, 5);
	const values = METRICS.map((key) => new Map(data[key].assets.map((asset) => [asset.entity_id, asset])));
	return (
		<div className="insight-dashboard visibility-dashboard">
			<p className="insight-context">
				{data.samples === 0
					? t("insights.empty")
					: t("insights.denominator", { n: data.samples, before: data.samples_previous })}
			</p>
			<Tabs
				value={metric}
				onValueChange={(next) => {
					if (METRICS.includes(next as typeof metric)) setMetric(next as typeof metric);
				}}
				className="metric-workspace"
			>
				<div className="metric-summaries">
					<TabsList aria-label={t("insights.metricView")}>
						{METRICS.map((key, index) => {
							const Icon = METRIC_ICONS[index];
							return (
								<TabsTrigger key={key} value={key} className="metric-summary">
									<span className="metric-summary-label">
										<InsightIcon tone={METRIC_TONES[index]}>
											<Icon />
										</InsightIcon>
										{t(`metric.${key}`)}
									</span>
									<span className="metric-summary-value num">{value(locale, key, data[key].value)}</span>
									<span className="metric-summary-foot">
										{data[key].value === null ? (
											// Answers were collected and none named the brand: that is
											// a finding, and "no data" read as though nothing had run.
											t(data.samples > 0 ? "insights.notNamed" : "insights.noData")
										) : (
											<Delta block={data[key]} samples={data.samples} samplesPrevious={data.samples_previous} />
										)}
									</span>
								</TabsTrigger>
							);
						})}
					</TabsList>
				</div>
				{METRICS.map((key) => (
					<TabsContent key={key} value={key}>
						<Section
							title={t(key === "share_of_voice" ? "insights.voiceDistribution" : "insights.metricTrend", {
								metric: t(`metric.${key}`),
							})}
							icon={
								<InsightIcon tone="detect">
									<LineChart />
								</InsightIcon>
							}
							lede={t(`metric.${key}.hint`)}
							action={
								<Button variant="default" onClick={() => download(key, metricCsv(data[key], t("insights.brand")))}>
									<Download aria-hidden />
									{t("table.export")}
								</Button>
							}
						>
							<div className="insight-chart">
								{key === "share_of_voice" ? (
									<Donut assets={data[key].assets} />
								) : (
									<Series distinguishLines metric={key} points={data[key].series} assets={data[key].assets} />
								)}
							</div>
						</Section>
					</TabsContent>
				))}
			</Tabs>
			<Section
				title={t("insights.brandComparison")}
				icon={
					<InsightIcon tone="analyse">
						<Users />
					</InsightIcon>
				}
				action={
					// Alone on the axis there is nothing to rank against, and the
					// way out is naming who you compete with.
					data.assets.length < 2 ? (
						<DrillLink to={readingPath(brand(entityId), "competitors")}>{t("insights.addRivals")}</DrillLink>
					) : (
						<span className="insight-context">
							{t(`metric.${metric}`)} · {t("insights.rank")}{" "}
							{block.rank === null ? "—" : `${block.rank} / ${block.ranked}`}
						</span>
					)
				}
			>
				<div className="table-scroll">
					<Table variant="resource">
						<TableHeader>
							<TableRow>
								<TableHead>{t("insights.brand")}</TableHead>
								{METRICS.map((key) => (
									<TableHead className="num" key={key}>
										{t(`metric.${key}`)} <Hint text={t(`metric.${key}.hint`)} />
									</TableHead>
								))}
							</TableRow>
						</TableHeader>
						<TableBody>
							{brands.length === 0 ? (
								<TableRow>
									<TableCell colSpan={4}>{t("insights.noData")}</TableCell>
								</TableRow>
							) : (
								brands.map((brand) => (
									<TableRow key={brand.entity_id} className={brand.is_you ? "is-you" : undefined}>
										<TableCell>{brand.name}</TableCell>
										{METRICS.map((key, index) => {
											const asset = values[index].get(brand.entity_id);
											return (
												<TableCell
													key={key}
													className="num mono"
													title={`${t("insights.rank")}: ${asset?.rank ?? "—"}`}
												>
													{value(locale, key, asset?.value)}
												</TableCell>
											);
										})}
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</div>
				{data.assets.length > 5 ? (
					<Button variant="ghost" onClick={() => setExpanded(!expanded)}>
						{t(expanded ? "insights.collapse" : "insights.expand")}
					</Button>
				) : null}
			</Section>
			<Topics rows={data.topics} />
		</div>
	);
}

type Column = "topic" | "prompts" | "samples" | "visibility" | "citation";

/**
 * The topic axis, in the two forms it is worth reading.
 *
 * A ranked table says where you stand subject by subject. The matrix says
 * something the table cannot: a topic high on visibility and low on citations
 * is one the assistants answer without needing your page, which is a different
 * problem — and a different piece of work — from not being in the answer.
 */
function Topics({ rows }: { rows: TopicRow[] }) {
	const t = useT();
	const { locale } = useI18n();
	const sort = useSort<Column>("samples");
	const [view, setView] = useState("table");

	if (rows.length === 0) return null;

	const measured = rows.filter((row) => row.visibility !== null && row.citation_share !== null);

	return (
		<Tabs value={view} onValueChange={setView} className="topic-views">
			<Section
				title={t("insights.byTopic")}
				lede={t(view === "matrix" ? "insights.matrixHint" : "insights.byTopicHint")}
				icon={
					<InsightIcon tone="generate">
						<ListFilter />
					</InsightIcon>
				}
				action={
					<TabsList aria-label={t("insights.topicView")}>
						<TabsTrigger value="table">{t("insights.tableView")}</TabsTrigger>
						<TabsTrigger value="matrix">{t("insights.matrix")}</TabsTrigger>
					</TabsList>
				}
			>
				<TabsContent value="table">
					<ResourceOptions
						translate={t}
						className="analytics-table-tools"
						sort={sort.config([
							{ id: "topic", label: t("insights.topic") },
							{ id: "prompts", label: t("insights.prompts") },
							{ id: "samples", label: t("insights.runs") },
							{ id: "visibility", label: t("metric.visibility") },
							{ id: "citation", label: t("metric.citation_share") },
						])}
					/>
					<div className="table-scroll">
						<Table variant="resource">
							<TableHeader>
								<TableRow>
									<SortHeader className="wrap" label={t("insights.topic")} column="topic" sort={sort} />
									{/* Both counts: how many prompts the topic holds, and how
								    many answers they produced in the window. The answers
								    are the denominator; a topic with one prompt sampled
								    forty times and one with forty prompts sampled once
								    are the same column and not the same finding. */}
									<SortHeader className="num" label={t("insights.prompts")} column="prompts" sort={sort} />
									<SortHeader className="num" label={t("insights.runs")} column="samples" sort={sort} />
									<SortHeader className="num" label={t("metric.visibility")} column="visibility" sort={sort} />
									<SortHeader className="num" label={t("metric.citation_share")} column="citation" sort={sort} />
									<TableHead className="num">{t("insights.rank")}</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{sort
									.apply(rows, (row, column) =>
										column === "topic"
											? row.topic
											: column === "prompts"
												? row.prompts
												: column === "visibility"
													? row.visibility
													: column === "citation"
														? row.citation_share
														: row.samples,
									)
									.map((row) => (
										<TableRow key={row.topic || "-"}>
											<TableCell className="wrap">
												{row.topic || <span className="faint">{t("insights.noTopic")}</span>}
											</TableCell>
											<TableCell className="num mono">{row.prompts}</TableCell>
											<TableCell className="num mono">{row.samples}</TableCell>
											<TableCell className="num mono">{value(locale, "visibility", row.visibility)}</TableCell>
											<TableCell className="num mono">{value(locale, "citation_share", row.citation_share)}</TableCell>
											<TableCell className="num mono">
												{row.rank === null || row.ranked < 2 ? "—" : `${row.rank} / ${row.ranked}`}
											</TableCell>
										</TableRow>
									))}
							</TableBody>
						</Table>
					</div>
				</TabsContent>
				<TabsContent value="matrix">
					<Matrix
						xLabel={t("metric.citation_share")}
						yLabel={t("metric.visibility")}
						quadrants={{
							lowLow: t("insights.quadrant.absent"),
							lowHigh: t("insights.quadrant.unlinked"),
							highLow: t("insights.quadrant.uncredited"),
							highHigh: t("insights.quadrant.strong"),
						}}
						points={measured.map((row) => ({
							key: row.topic || "-",
							label: row.topic || t("insights.noTopic"),
							x: row.citation_share ?? 0,
							y: row.visibility ?? 0,
							weight: row.samples,
						}))}
					/>
				</TabsContent>
			</Section>
		</Tabs>
	);
}
